import { DONATION_ITEM_ID } from '@helpers/cart/cart-hooks';
import { CART_UPDATED_EVENT, readStoredCartOrder } from '@helpers/cart/cart-storage';
import { isPrizeDrawLink, trackBuyTicketsButtonClick, trackPromoCardViewItem } from '@helpers/gtm-hooks';

const PRIZEHOMES_SITE_CLASS = 'prizehomes';
const BUY_TICKETS_DRAW_NUMBER_PARAM = 'drawNumber';
const BUY_TICKETS_DRAW_TITLE_PREFIX = 'Draw ';
const BUY_TICKETS_DRAW_TITLE_SELECTOR = '.yt-o-prize-home-page-heading__super-heading';
const BUY_TICKETS_BUTTON_SELECTOR = 'a.yt-btn-primary:not(.disabled)';
const BUY_TICKETS_STICKY_BUTTON_SELECTOR = '.sticky-cta-button';
const BUY_TICKETS_SECTION_SELECTORS: { section: 'hero_banner' | 'header'; selector: string }[] = [
  { section: 'hero_banner', selector: '.yt-o-prize-info' },
  { section: 'hero_banner', selector: '.hero-banner-cta' },
  { section: 'header', selector: '.yt-o-header' },
];

const resolveBuyTicketsSiteSection = (anchor: HTMLAnchorElement): 'sticky' | 'hero_banner' | 'header' | 'body' => {
  if (anchor.closest(BUY_TICKETS_STICKY_BUTTON_SELECTOR)) return 'sticky';

  for (const { section, selector } of BUY_TICKETS_SECTION_SELECTORS) {
    if (anchor.closest(selector)) return section;
  }

  return 'body';
};

const resolveBuyTicketsDrawInfo = (anchor: HTMLAnchorElement) => {
  const [addressable] = (anchor.getAttribute('href') ?? '').split('#');
  const [, query] = addressable.split('?');
  const drawId = query ? (new URLSearchParams(query).get(BUY_TICKETS_DRAW_NUMBER_PARAM) ?? '') : '';
  const heading = document.querySelector<HTMLElement>(BUY_TICKETS_DRAW_TITLE_SELECTOR)?.textContent?.trim();
  const drawName = heading || (drawId ? BUY_TICKETS_DRAW_TITLE_PREFIX + drawId : '');

  return { drawId, drawName };
};

const handleBuyTicketsClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  const anchor = target?.closest<HTMLAnchorElement>(`${BUY_TICKETS_BUTTON_SELECTOR}, ${BUY_TICKETS_STICKY_BUTTON_SELECTOR}`);

  if (!anchor) return;

  const siteSection = resolveBuyTicketsSiteSection(anchor);
  const { drawId, drawName } = resolveBuyTicketsDrawInfo(anchor);

  trackBuyTicketsButtonClick({
    site_section: siteSection,
    click_text: anchor.textContent?.trim() ?? '',
    draw_name: drawName,
    draw_id: drawId,
  });
};

const setupBuyTicketsTracking = () => {
  document.addEventListener('click', handleBuyTicketsClick);
};

const handlePrizeDrawLinkClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  const anchor = target?.closest<HTMLAnchorElement>('a');

  if (anchor?.closest('[data-gtm-tracked]')) return;

  const href = anchor?.getAttribute('href') ?? undefined;

  if (!isPrizeDrawLink(href)) return;

  trackPromoCardViewItem(href!, anchor?.textContent?.trim());
};

const setupPrizeDrawLinkTracking = () => {
  document.addEventListener('click', handlePrizeDrawLinkClick);
};

const updateCartCounters = (root: ParentNode) => {
  const miniCarts = root.querySelectorAll<HTMLElement>('.cart-counter');

  if (miniCarts.length === 0) return;

  const cartStorage = readStoredCartOrder();
  const itemCount =
    cartStorage?.orderItems?.filter((item) => item.id !== DONATION_ITEM_ID).reduce((total, item) => total + (item.ticketCount ?? 0), 0) || 0;

  miniCarts.forEach((miniCart) => {
    miniCart.classList.toggle('d-none', itemCount === 0);
    miniCart.classList.toggle('double-digits', itemCount > 9);
    miniCart.textContent = itemCount.toString();
  });
};

const setupHeader = () => {
  const el = document.querySelector('.yt-o-header');

  if (!el) return;

  updateCartCounters(el);
  window.addEventListener(CART_UPDATED_EVENT, () => updateCartCounters(el));

  const buttonMenu = el.querySelector<HTMLElement>('.yt-o-mobile-nav__nav-toggle');
  const buttonClose = el.querySelector<HTMLElement>('.yt-o-mobile-nav__close-btn');

  const navList = el.querySelector<HTMLElement>('.mobile-site-nav-menu');
  const isYourtown = document.body.classList.contains('yourtown');
  const pageContent = isYourtown
    ? Array.from(el.parentElement?.children ?? []).filter((element) => element !== el)
    : [];
  const pageContentState = pageContent.map((element) => ({
    element,
    ariaHidden: element.getAttribute('aria-hidden'),
    inert: element.hasAttribute('inert'),
  }));

  const setPageContentAccessibility = (hidden: boolean) => {
    pageContentState.forEach(({ element, ariaHidden, inert }) => {
      if (hidden) {
        element.setAttribute('aria-hidden', 'true');
        element.setAttribute('inert', '');
      } else {
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
        if (!inert) element.removeAttribute('inert');
      }
    });
  };

  const openMenu = () => {
    buttonMenu?.classList.add('open');
    buttonMenu?.setAttribute('aria-expanded', 'true');
    navList?.classList.add('open');
    if (isYourtown) navList?.setAttribute('aria-hidden', 'false');
    setPageContentAccessibility(true);
    document.body.style.overflow = 'hidden';
  };

  const navItemsWrapper = el.querySelector<HTMLElement>('.nav-items-wrapper');

  const resetNavPanels = () => {
    if (!navItemsWrapper) return;
    navItemsWrapper.querySelectorAll<HTMLElement>('[data-nav-panel]').forEach((p) => {
      p.classList.remove('nav-panel--active', 'nav-panel--exit-left');
    });
    const root = navItemsWrapper.querySelector<HTMLElement>('[data-nav-panel="root"]');

    if (root) {
      root.classList.add('nav-panel--active');
    }
    el.querySelectorAll<HTMLElement>('[data-nav-expand]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (isYourtown) {
      navItemsWrapper.querySelectorAll<HTMLElement>('[data-nav-panel]').forEach((panel) => {
        const active = panel.classList.contains('nav-panel--active');
        panel.setAttribute('aria-hidden', String(!active));
        panel.toggleAttribute('hidden', !active);
      });
    }
  };

  const closeMenu = () => {
    // Fires on any outside click, so only release the scroll lock this menu actually took.
    const wasOpen = navList?.classList.contains('open') ?? false;

    buttonMenu?.classList.remove('open');
    buttonMenu?.setAttribute('aria-expanded', 'false');
    navList?.classList.remove('open');
    if (isYourtown) navList?.setAttribute('aria-hidden', 'true');
    setPageContentAccessibility(false);
    document.body.style.overflow = '';
    resetNavPanels();
  };

  if (isYourtown) navList?.setAttribute('aria-hidden', 'true');
  resetNavPanels();

  if (buttonClose) {
    buttonClose.addEventListener('click', closeMenu);
  }

  if (!buttonMenu || !navList) return;

  buttonMenu.addEventListener('click', () => {
    openMenu();
  });

  if (typeof navList.onOutsideClick === 'function') {
    navList.onOutsideClick(() => {
      closeMenu();
    }, [buttonMenu]);
  }

  // Nav items: nested sub-panels
  el.querySelectorAll<HTMLElement>('[data-nav-expand]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!navItemsWrapper) return;
      const targetId = btn.getAttribute('data-nav-target');
      const targetPanel = navItemsWrapper.querySelector<HTMLElement>(`[data-nav-panel="${targetId}"]`);
      const currentPanel = navItemsWrapper.querySelector<HTMLElement>('.nav-panel--active');

      if (!targetPanel || !currentPanel) return;
      currentPanel.classList.remove('nav-panel--active');
      currentPanel.classList.add('nav-panel--exit-left');
      targetPanel.classList.remove('nav-panel--exit-left');
      targetPanel.classList.add('nav-panel--active');
      btn.setAttribute('aria-expanded', 'true');
      if (isYourtown) {
        currentPanel.setAttribute('aria-hidden', 'true');
        currentPanel.setAttribute('hidden', '');
        targetPanel.setAttribute('aria-hidden', 'false');
        targetPanel.removeAttribute('hidden');
      }
    });
  });

  el.querySelectorAll<HTMLElement>('[data-nav-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!navItemsWrapper) return;
      const currentPanel = btn.closest<HTMLElement>('[data-nav-panel]');
      const parentId = currentPanel?.getAttribute('data-nav-parent');

      if (parentId) {
        const parentPanel = navItemsWrapper.querySelector<HTMLElement>(`[data-nav-panel="${parentId}"]`);
        if (parentPanel && currentPanel) {
          currentPanel.classList.remove('nav-panel--active');
          currentPanel.classList.add('nav-panel--exit-left');
          parentPanel.classList.remove('nav-panel--exit-left');
          parentPanel.classList.add('nav-panel--active');
          if (isYourtown) {
            currentPanel.setAttribute('aria-hidden', 'true');
            currentPanel.setAttribute('hidden', '');
            parentPanel.setAttribute('aria-hidden', 'false');
            parentPanel.removeAttribute('hidden');
          }
          parentPanel.querySelectorAll<HTMLElement>('[data-nav-expand]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
          return;
        }
      }

      resetNavPanels();
    });
  });

  // Handle desktop mega menu hover interactions
  const megaMenu = el.querySelector<HTMLElement>('.desktop-mega-menu-wrapper');
  const navItems = el.querySelectorAll<HTMLElement>('.nav-items-left button.nav-items-left__item');
  const megaMenuCloseBtn = megaMenu?.querySelectorAll<HTMLElement>('.mega-menu__close-btn');

  if (isYourtown) megaMenu?.setAttribute('aria-hidden', 'true');

  const HOVER_DELAY_MS = 150;
  const CLOSE_DELAY_MS = 100;

  let activeItem: HTMLElement | null = null;
  let openTimer: ReturnType<typeof setTimeout> | null = null;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;
  let previouslyFocusedElement: HTMLElement | null = null;

  const resetOpenTimer = () => {
    if (openTimer !== null) {
      clearTimeout(openTimer);
      openTimer = null;
    }
  };

  const resetCloseTimer = () => {
    if (closeTimer !== null) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  };

  // Focus trap implementation for mega menu
  const getFocusableElements = (): HTMLElement[] => {
    if (!megaMenu) return [];
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    return Array.from(megaMenu.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
      (el) => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null
    );
  };

  const focusFirstElement = () => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  };

  const focusLastElement = () => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[focusableElements.length - 1].focus();
    }
  };

  const handleMegaMenuKeydown = (e: KeyboardEvent) => {
    if (!megaMenu?.classList.contains('open')) return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const currentFocused = document.activeElement as HTMLElement;
    const focusedIndex = focusableElements.indexOf(currentFocused);

    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift + Tab: move backwards
        if (focusedIndex <= 0) {
          focusLastElement();
        } else {
          focusableElements[focusedIndex - 1].focus();
        }
      } else {
        // Tab: move forwards
        if (focusedIndex >= focusableElements.length - 1) {
          focusFirstElement();
        } else {
          focusableElements[focusedIndex + 1].focus();
        }
      }
    }
  };

  const navItemsArray = Array.from(navItems);

  const openMegaMenu = (item: HTMLElement) => {
    resetCloseTimer();
    if (activeItem && activeItem !== item) {
      activeItem.classList.remove('active');
      if (activeItem.tagName.toLowerCase() === 'button') {
        activeItem.setAttribute('aria-expanded', 'false');
      }
    }
    activeItem = item;
    item.classList.add('active');

    if (item.tagName.toLowerCase() === 'a') {
      if (megaMenu?.classList.contains('open')) {
        closeTimer = setTimeout(() => {
          megaMenu?.classList.remove('open');
        }, CLOSE_DELAY_MS);
      }
      return;
    }

    if (isYourtown) navItems.forEach((navItem) => navItem.setAttribute('aria-expanded', String(navItem === item)));

    const index = navItemsArray.indexOf(item);
    megaMenu?.querySelectorAll<HTMLElement>('[data-mega-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.megaPanel !== String(index);
      if (panel.dataset.megaPanel === String(index)) {
        panel.setAttribute('aria-hidden', 'false');
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
    });

    megaMenu?.classList.add('open');
    if (isYourtown) megaMenu?.setAttribute('aria-hidden', 'false');

    // Set background content as hidden from screen readers
    const pageContent = Array.from(el.parentElement?.children ?? []).filter((element) => element !== el);
    pageContent.forEach((element) => {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    });

    // Store focus and focus first element in mega menu
    previouslyFocusedElement = item;    
    focusFirstElement();
  };

  const scheduleClose = () => {
    closeTimer = setTimeout(() => {
      if (activeItem?.tagName.toLowerCase() === 'button') {
        activeItem.setAttribute('aria-expanded', 'false');
      }
      activeItem?.classList.remove('active');
      if (isYourtown) navItems.forEach((navItem) => navItem.setAttribute('aria-expanded', 'false'));

      // Reset mega menu panels to be hidden from screen readers
      megaMenu?.querySelectorAll<HTMLElement>('[data-mega-panel]').forEach((panel) => {
        panel.setAttribute('aria-hidden', 'true');
      });

      activeItem = null;
      megaMenu?.classList.remove('open');
      if (isYourtown) megaMenu?.setAttribute('aria-hidden', 'true');

      // Restore background content visibility
      const pageContent = Array.from(el.parentElement?.children ?? []).filter((element) => element !== el);
      pageContent.forEach((element) => {
        element.removeAttribute('aria-hidden');
        element.removeAttribute('inert');
      });

      // Return focus to the previously focused menu item
      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
      }
    }, CLOSE_DELAY_MS);
  };

  navItems.forEach((item) => {
    item.addEventListener('mouseenter', () => {
      resetOpenTimer();
      if (megaMenu?.classList.contains('open')) {
        openMegaMenu(item);
      } else {
        openTimer = setTimeout(() => openMegaMenu(item), HOVER_DELAY_MS);
      }
    });

    item.addEventListener('click', () => {
      resetOpenTimer();
      if (megaMenu?.classList.contains('open')) {
        openMegaMenu(item);
      } else {
        openTimer = setTimeout(() => openMegaMenu(item), HOVER_DELAY_MS);
      }
    });

    item.addEventListener('mouseleave', () => {
      resetOpenTimer();
      scheduleClose();
    });

    item.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        item.dispatchEvent(clickEvent);
      }
    });

    if (isYourtown) {
      item.addEventListener('blur', (e: FocusEvent) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        const isMovingToNavItem = relatedTarget && navItems.some((navItem) => navItem.contains(relatedTarget));
        const isMovingToMegaMenu = relatedTarget && megaMenu?.contains(relatedTarget);
        if (!isMovingToNavItem && !isMovingToMegaMenu) {
          resetOpenTimer();
          scheduleClose();
        }
      });
    }
  });

  megaMenu?.addEventListener('mouseenter', () => {
    resetCloseTimer();
  });

  megaMenu?.addEventListener('mouseleave', () => {
    scheduleClose();
  });


  if (isYourtown && megaMenu) {
    megaMenu.addEventListener('focusout', (e: FocusEvent) => {

      const relatedTarget = e.relatedTarget as HTMLElement;


      const isLeavingMenu = relatedTarget && !megaMenu.contains(relatedTarget) && !navItems.some((item) => item.contains(relatedTarget));

      if (isLeavingMenu) {

        setTimeout(() => {
          resetOpenTimer();
          scheduleClose();
        }, 50);
      }
    });
  }

  megaMenu?.addEventListener('keydown', handleMegaMenuKeydown);

  megaMenuCloseBtn?.forEach((btn) => {
    btn.addEventListener('click', () => {
      scheduleClose();
    });
  });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && megaMenu?.classList.contains('open')) {
      scheduleClose();
    }
  });
};

setupHeader();

if (document.body.classList.contains(PRIZEHOMES_SITE_CLASS)) {
  setupBuyTicketsTracking();
  setupPrizeDrawLinkTracking();
}

export { };
